// src/pages/UserPages/UserAppointmentsPage.js

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Link,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Swal from "sweetalert2";

// Hooks e Context
import { useThemeMode } from "../../components/ThemeModeContext";

// Componentes Personalizados
import axiosInstance from "../../Services/axiosConfig";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import FeedbackButton from "../../components/FeedbackButton";
import FeedbackSnackbar from "../../components/FeedbackSnackbar";
import LoadingIndicator from "../../components/LoadingIndicator";

// Ícones para exibição no Card
import EventIcon from "@mui/icons-material/Event";
import BusinessIcon from "@mui/icons-material/Business";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

/** ----------------------------------------------------------------------------
 * Componente de Cartão de Agendamento (AppointmentCard)
 * --------------------------------------------------------------------------- */
function AppointmentCard({ appointment, loading, onCancel, onNextSession }) {
  // Menu de opções (3 pontinhos)
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Cancela o agendamento
  const handleCancel = () => {
    handleMenuClose();
    if (!loading) {
      onCancel(appointment.id);
    }
  };

  // Marca a próxima sessão
  const handleNextSession = () => {
    handleMenuClose();
    if (!loading) {
      onNextSession(appointment.id);
    }
  };

  // Copia o endereço para a área de transferência
  const copyAddress = () => {
    if (appointment.address) {
      navigator.clipboard.writeText(appointment.address);
    }
  };

  return (
    <Card
      elevation={3}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Cabeçalho: Título do Serviço + Menu */}
        <Box display="flex" justifyContent="space-between" alignItems="start">
          <Typography variant="h6" gutterBottom>
            {appointment.service}
          </Typography>
          <IconButton
            aria-label="menu"
            onClick={handleMenuOpen}
            disabled={loading}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        {/* Menu suspenso (Delete, Next Session) */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {appointment.status === "Agendado" && (
            <MenuItem onClick={handleCancel} disabled={loading}>
              Deletar/Cancelar
            </MenuItem>
          )}
          {appointment.status === "Aguardando" && (
            <MenuItem onClick={handleNextSession} disabled={loading}>
              Marcar Próxima Sessão
            </MenuItem>
          )}
        </Menu>

        {/* Data/Evento */}
        <Typography variant="body2" gutterBottom>
          <EventIcon fontSize="small" sx={{ mr: 0.5 }} />
          {new Date(appointment.date).toLocaleString()}
        </Typography>

        {/* Empresa */}
        <Typography variant="body2" gutterBottom>
          <BusinessIcon fontSize="small" sx={{ mr: 0.5 }} />
          {appointment.company}
        </Typography>

        {/* Endereço (link para Google Maps + copiar) */}
        {appointment.address && (
          <Box display="flex" alignItems="center" gap={1} mt={1}>
            <LocationOnIcon fontSize="small" />
            <Link
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                appointment.address
              )}`}
              target="_blank"
              rel="noopener"
              variant="body2"
              underline="hover"
              sx={{ color: "text.primary" }}
            >
              {appointment.address}
            </Link>
            <IconButton
              aria-label="Copiar endereço"
              size="small"
              onClick={copyAddress}
              disabled={!appointment.address || loading}
            >
              <ContentCopyIcon fontSize="inherit" />
            </IconButton>
          </Box>
        )}

        {/* Sector */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Setor: {appointment.sector}
        </Typography>

        {/* Status */}
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            fontWeight: "bold",
            color:
              appointment.status === "Agendado"
                ? "success.main"
                : appointment.status === "Aguardando"
                ? "warning.main"
                : "error.main",
          }}
        >
          Status: {appointment.status}
        </Typography>
      </CardContent>
    </Card>
  );
}

/** ----------------------------------------------------------------------------
 * Página Principal: UserAppointmentsPage
 * --------------------------------------------------------------------------- */
export default function UserAppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDate, setFilterDate] = useState("");

  // Estados para feedback via Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const token = localStorage.getItem("authToken");

  // Modo de tema (light/dark)
  const { mode } = useThemeMode();

  // Cria tema dinamicamente
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === "light" ? "#FAF5E4" : "#121212",
            paper: mode === "light" ? "#FFFFFF" : "#1E1E1E",
          },
          text: {
            primary: mode === "light" ? "#1A202C" : "#FFFFFF",
          },
        },
      }),
    [mode]
  );

  // Busca agendamentos
  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`user/appointments/${token}/`);
      if (response.data.status === "success") {
        const dataArr = Object.values(response.data.data);
        setAppointments(dataArr);
        setFilteredAppointments(dataArr);
      } else {
        Swal.fire("Error", "Failed to fetch appointments", "error");
      }
    } catch (error) {
      console.error("Error fetching appointments", error);
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  // Filtra por data
  const filterAppointmentsByDate = async () => {
    if (!filterDate) {
      setFilteredAppointments(appointments);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.get(`user/appointment/${token}/${filterDate}/`);
      if (response.data.status === "success") {
        const arr = Object.values(response.data.data);
        setFilteredAppointments(arr);
      } else {
        setFilteredAppointments([]);
      }
    } catch (error) {
      console.error("Error filtering appointments", error);
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  // Limpa o filtro de data
  const clearFilter = () => {
    setFilterDate("");
    setFilteredAppointments(appointments);
  };

  // Cancela
  const cancelAppointment = async (appointmentId) => {
    Swal.fire({
      title: "Cancelar o Agendamento?",
      text: "Voce tem certeza que deseja cancelar este serviço? Esta ação não pode ser desfeita sem contactar o prestador de serviço.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, cancelar serviço",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await axiosInstance.post(`user/appointment/`, {
            authtoken: token,
            appointment_id: appointmentId,
          });
          if (response.data.status === "success") {
            Swal.fire("Cancelado!", "Seu serviço foi cancelado.", "success");
            fetchAppointments();
          } else {
            Swal.fire("Error", response.data.message, "error");
          }
        } catch (error) {
          console.error("Erro ao cancelar o agendamento", error);
          Swal.fire("Erro ao cancelar", "Algo deu errado", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Marca a próxima sessão
  const handleNextSession = async (appointmentId) => {
    Swal.fire({
      title: "Marcar Próxima Sessão?",
      text: "Deseja agendar a próxima sessão para este atendimento?",
      icon: "info",
      showCancelButton: true,
      confirmButtonText: "Sim, Agendar",
      cancelButtonText: "Não",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await axiosInstance.post(`user/schedule-next/`, {
            authtoken: token,
            appointment_id: appointmentId,
          });
          if (response.data.status === "success") {
            Swal.fire("Sucesso!", "Próxima sessão agendada.", "success");
            fetchAppointments();
          } else {
            Swal.fire("Error", response.data.message, "error");
          }
        } catch (error) {
          console.error("Erro ao agendar a próxima sessão", error);
          Swal.fire("Erro", "Algo deu errado", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Fecha Snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="background.default">
        <Navbar />

        <Box p={3} flexGrow={1}>
          <Typography
            variant="h4"
            gutterBottom
            textAlign="center"
            fontWeight="bold"
          >
            Meus Agendamentos
          </Typography>

          {/* Filtro de Data */}
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            mb={3}
            justifyContent="center"
            alignItems="center"
          >
            <TextField
              label="Filtrar por Data"
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Button
              variant="contained"
              color="primary"
              onClick={filterAppointmentsByDate}
              disabled={loading}
              sx={{ minWidth: "120px" }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Filter"}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={clearFilter}
              sx={{ minWidth: "120px" }}
            >
              Limpar filtros
            </Button>
          </Box>

          {/* Lista de Agendamentos */}
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
              <LoadingIndicator />
            </Box>
          ) : filteredAppointments.length > 0 ? (
            <Grid container spacing={3}>
              {filteredAppointments.map((appointment) => (
                <Grid item xs={12} sm={6} md={4} key={appointment.id}>
                  <AppointmentCard
                    appointment={appointment}
                    loading={loading}
                    onCancel={cancelAppointment}
                    onNextSession={handleNextSession}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" textAlign="center" mt={4}>
              Sem agendamentos para exibir.
            </Typography>
          )}
        </Box>

        {/* Feedback Snackbar */}
        <FeedbackSnackbar
          open={openSnackbar}
          handleClose={handleCloseSnackbar}
          message={snackbarMsg}
          severity={snackbarSeverity}
        />

        <FeedbackButton />
        <Footer />
      </Box>
    </ThemeProvider>
  );
}
